import { Context, Dispatch, ReactNode, Reducer, useEffect, useReducer, useState } from 'react';

interface ReducerContextType<State, Action> {
  state: State;
  dispatch: Dispatch<Action>;
}

interface Props<State, Action> {
  reducer: Reducer<State, Action>;
  defaultState?: () => State;
  initialState?: () => Partial<State>;
  children: ReactNode;
  Context: Context<ReducerContextType<State, Action>>;
}

export const ReducerProviderInner = <State, Action>({
  Context,
  children,
  reducer,
  defaultState,
  initialState,
}: Props<State, Action>) => {
  const [state, dispatch]: [State, Dispatch<Action>] = useReducer(reducer, {
    ...(defaultState ? defaultState() : {}),
    ...(initialState ? initialState() : {}),
  } as State);

  return <Context.Provider value={{ state, dispatch }}>{children}</Context.Provider>;
};

type MountStrategy = 'afterPaint' | 'immediate';

export const ProvideReducer = <State, Action>({
  children,
  mountStrategy = 'afterPaint',
  ...props
}: Props<State, Action> & { mountStrategy?: MountStrategy }) => {
  const [mounted, setMounted] = useState(mountStrategy === 'immediate');

  useEffect(() => {
    if (mountStrategy === 'immediate') return;
    setMounted(true);
  }, [mountStrategy]);

  if (!mounted) {
    return null;
  }

  return <ReducerProviderInner<State, Action> {...props}>{children}</ReducerProviderInner>;
};
